import { useMemo, useState } from "react";
import Bugsnag from "@bugsnag/js";
import { useDispatch } from "react-redux";
import { ChunkLoadRecoveryModal } from "components/chunk-load-recovery-modal";
import { Button } from "components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { FIELDS } from "constants/PostFormFields";
import { openModal } from "libs/modals";
import { POST_LOCATION_MODAL, POST_TAGS_MODAL } from "../modals/modal-types";
import {
  ensurePostTagsModalRegistered,
  ensurePostLocationModalRegistered,
} from "../modals/lazy-registration";
import { toast } from "react-toastify";
import { POST_TYPES } from "constants/PostsView";
import { ADD_COLLABORATOR_MODAL } from "../components/CollaboratorsModal/index";
import type { FormPlatformType } from "shared/types/post";
import type { AccountType } from "shared/types/accounts";
import { isChunkLoadError } from "utils/is-chunk-load-error";
import { reloadPage } from "utils/reload-page";
import { toError } from "utils/to-error";
import PlatformUsage from "../PlatformUsage/PlatformUsage";

const FIGMA_ADD_BUTTON_WIDTHS: Record<string, number> = {
  "Add location": 112,
  "Add collaborators": 142,
  "Add user tags": 120,
  "Add product tags": 139,
  "Add alt text": 106,
};

type PostTagMediaFile = {
  isVideo?: boolean;
  altText?: string | string[];
};

type PostTagAccount = Pick<AccountType, "_id"> & {
  platformName?: string;
  platformType?: string;
};

type Collaborator = {
  username: string;
  profilePictureUrl?: string;
};

type PostTagLocation = { name?: string } | null | undefined;

type PostTagValue = {
  location?: PostTagLocation;
  collaboratingIgUsers?: Collaborator[];
  tags?: unknown[];
  productTags?: unknown[];
  mediaFiles?: PostTagMediaFile[];
  type?: string;
  altText?: string;
  accountIds?: string[];
  accounts: PostTagAccount[];
};

const EMPTY_TAGS: unknown[] = [];

const DisabledOptionHelp = ({
  label,
  message,
}: {
  label: string;
  message: string;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="flex h-6 w-[15px] shrink-0 cursor-pointer items-center justify-center rounded-full p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
          aria-label={`Why ${label} is disabled`}
          aria-expanded={open}
          onClick={() => setOpen(true)}
        >
          <span
            className="flex size-[15px] shrink-0 items-center justify-center rounded-full bg-neutral-700 font-inter text-xs font-normal leading-[15px] text-white"
            aria-hidden="true"
          >
            ?
          </span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-64 text-left">
        {message}
      </TooltipContent>
    </Tooltip>
  );
};

const ensureModalAvailable = async (
  register: () => Promise<void>,
  errorMessage: string,
  context: string,
  onChunkLoadError: () => void
): Promise<boolean> => {
  try {
    await register();
    return true;
  } catch (error) {
    const normalizedError = toError(error);
    Bugsnag.notify(normalizedError, (event) => {
      event.context = context;
    });

    if (isChunkLoadError(normalizedError)) {
      onChunkLoadError();
      return false;
    }

    toast.error(errorMessage);
    return false;
  }
};
interface PostTagsProps {
  post: PostTagValue;
  selectedPlatform: FormPlatformType;
  platforms: unknown[];
  visibleFields: string[];
  className?: string;
  onChange: (field: string, value: unknown) => void;
  handleOpenAddAlt: () => void;
  isSummary?: boolean;
  hasTikTokPosting?: boolean;
}

const PostTags = ({
  post,
  selectedPlatform,
  platforms,
  visibleFields,
  className = "",
  onChange,
  handleOpenAddAlt,
  isSummary,
}: PostTagsProps) => {
  const dispatch = useDispatch();
  const [requiresReload, setRequiresReload] = useState(false);

  const location = post.location;

  const collaboratorIg = post.collaboratingIgUsers;
  const userTags = post.tags ?? EMPTY_TAGS;

  const productTags = post.productTags ?? EMPTY_TAGS;

  const canAltText = true;
  const hasImageMedia = useMemo(() => {
    if (!post?.mediaFiles || post.mediaFiles.length === 0) {
      return (
        post.type === POST_TYPES.PHOTO ||
        post.type === POST_TYPES.IMAGE ||
        post.type === POST_TYPES.MULTI_MEDIA
      );
    }

    return post.mediaFiles.some(
      (mediaFile: PostTagMediaFile) => !mediaFile.isVideo
    );
  }, [post]);

  const hasAlt = canAltText && hasImageMedia;

  const altText = useMemo(() => {
    if (!post) {
      return [];
    }
    if (!post.altText) {
      if (post.mediaFiles && post.mediaFiles.length < 1) {
        return [];
      } else {
        // continue - use post.alttext if no mediaFiles items
      }
    }
    if (post.mediaFiles && post.mediaFiles.length > 0) {
      return post.mediaFiles.flatMap((file: PostTagMediaFile) =>
        file.altText ? file.altText : []
      );
    } else {
      return post.altText ? [post.altText] : [];
    }
  }, [post]);

  function getIGAccountId() {
    let accountId;
    if ((post.accountIds?.length ?? 0) > 1) {
      const result = post.accounts.find(
        (perAccount: PostTagAccount) =>
          perAccount.platformName === "instagram" ||
          perAccount.platformType === "IG"
      );
      accountId = result?._id;
    } else {
      accountId = post.accountIds?.[0];
    }
    return accountId;
  }

  // We can't attach any tags to videos
  const firstMediaIsVideo = useMemo(() => {
    if (!post.mediaFiles || post.mediaFiles.length === 0) {
      return false;
    }

    let firstMedia = post.mediaFiles[0];

    return firstMedia.isVideo;
  }, [post]);

  const alertFirstMediaVideo = () => {
    toast.error("You can't tag the post if the first media asset is a video.");
  };

  const dispatchUserTagModal = async () => {
    if (!firstMediaIsVideo) {
      const isAvailable = await ensureModalAvailable(
        ensurePostTagsModalRegistered,
        "Failed to load the tag editor. Please try again.",
        "PostTags: load user tag modal",
        () => setRequiresReload(true)
      );
      if (!isAvailable) return;
      dispatch(
        openModal({
          type: POST_TAGS_MODAL,
          props: {
            type: "USER",
            post: post,
            onSubmit: (newTags: unknown[]) => {
              onChange(FIELDS.USER_TAGS, newTags);
            },
          },
        })
      );
    } else {
      alertFirstMediaVideo();
    }
  };

  const dispatchProductTagModal = async () => {
    if (firstMediaIsVideo) {
      alertFirstMediaVideo();
      return;
    }
    const isAvailable = await ensureModalAvailable(
      ensurePostTagsModalRegistered,
      "Failed to load the tag editor. Please try again.",
      "PostTags: load product tag modal",
      () => setRequiresReload(true)
    );
    if (!isAvailable) return;
    dispatch(
      openModal({
        type: POST_TAGS_MODAL,
        props: {
          type: "PRODUCT",
          post: post,
          onSubmit: (newTags: unknown[]) => {
            onChange(FIELDS.PRODUCT_TAGS, newTags);
          },
        },
      })
    );
  };

  const dispatchLocationModal = async () => {
    if (firstMediaIsVideo) {
      alertFirstMediaVideo();
      return;
    }
    const isAvailable = await ensureModalAvailable(
      ensurePostLocationModalRegistered,
      "Failed to load the location picker. Please try again.",
      "PostTags: load location modal",
      () => setRequiresReload(true)
    );
    if (!isAvailable) return;
    dispatch(
      openModal({
        type: POST_LOCATION_MODAL,
        props: {
          location: post.location,
          onSubmit: (newLocation: PostTagLocation) => {
            onChange(FIELDS.LOCATION_TAG, newLocation);
          },
        },
      })
    );
  };
  const dispatchCollaboratorModal = () => {
    dispatch(
      openModal({
        type: ADD_COLLABORATOR_MODAL,
        props: {
          collaboratorIg: collaboratorIg,
          beta: true,
          title: "Add Collaborators",
          notification:
            "If they accept, their usernames will be added to the Instagram post. It will be shared to their followers and shown on their profile. Maximum 3 collaborators.",
          limit: 3,
          onSubmit: (Collaborator: Collaborator[]) => {
            const collaboratorsNames = Collaborator.map(
              (aCollaborator: Collaborator) => {
                return {
                  username: aCollaborator.username,
                  profilePictureUrl: aCollaborator.profilePictureUrl,
                };
              }
            );
            onChange(FIELDS.COLLABORATOR_TAG, collaboratorsNames);
            if (Collaborator.length > 0) {
              toast.success(
                `Pow! ${Collaborator.length} collaborator(s) added. `
              );
            }
          },
          accountId: getIGAccountId(),
        },
      })
    );
  };
  // only show the field label and field if any of the fields are visible
  // so that it doesn't show a heading with an empty section
  if (
    visibleFields.includes(FIELDS.LOCATION_TAG) ||
    visibleFields.includes(FIELDS.COLLABORATOR_TAG) ||
    visibleFields.includes(FIELDS.USER_TAGS) ||
    visibleFields.includes(FIELDS.PRODUCT_TAGS) ||
    (hasAlt && visibleFields.includes(FIELDS.ALT_TEXT))
  ) {
    const hasProductTags = productTags.length > 0;
    const hasUserTags = userTags.length > 0;
    const altTextCount = altText.filter(
      (value) => typeof value === "string" && value.trim().length > 0
    ).length;

    const hasUserAndProductTags = hasProductTags && hasUserTags; // for access for legacy post to change tags value

    const productTagsCanBeChanged = !hasUserTags || hasUserAndProductTags;
    const userTagsCanBeChanged = !hasProductTags || hasUserAndProductTags;

    const renderPlatformLabel = (label: string, field: string) => (
      <div className="flex h-6 min-w-0 items-center gap-2">
        <span className="shrink-0 font-inter text-[14px] font-medium leading-5 text-neutral-800">
          {label}
        </span>
        <PlatformUsage
          platforms={platforms}
          field={field}
          selectedPlatform={selectedPlatform}
          variant="other-options"
        />
      </div>
    );

    const renderAction = ({
      label,
      onClick,
      disabled = false,
      testId,
      disabledMessage,
      selectedLabel,
    }: {
      label: string;
      onClick: () => void;
      disabled?: boolean;
      testId?: string;
      disabledMessage?: string;
      selectedLabel?: string;
    }) => {
      if (selectedLabel) {
        return (
          <div className="flex min-w-0 items-center gap-1">
            <span
              className="truncate font-inter text-[14px] font-medium leading-5 text-neutral-800"
              title={selectedLabel}
              data-testid="selected-option-value"
            >
              {selectedLabel}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="xs"
              className="size-6 shrink-0 rounded-lg p-1 text-neutral-800 shadow-none"
              disabled={disabled}
              onClick={onClick}
              data-testid={testId}
              aria-label={label}
            >
              <i
                className="fa-regular fa-pen-to-square text-xs"
                aria-hidden="true"
              />
            </Button>
          </div>
        );
      }

      const action = (
        <Button
          type="button"
          variant="black-secondary"
          size="xs"
          className="h-6 justify-self-start gap-2 rounded-lg px-2 py-1 font-inter text-xs font-medium leading-4 text-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.05)] [&_svg]:size-3"
          disabled={disabled}
          onClick={onClick}
          data-testid={testId}
          style={{ width: FIGMA_ADD_BUTTON_WIDTHS[label] }}
        >
          <span className="flex size-[15px] shrink-0 items-center justify-center">
            <i className="fa-regular fa-plus text-xs" aria-hidden="true" />
          </span>
          {label}
        </Button>
      );

      if (disabled && disabledMessage) {
        return (
          <div className="flex items-center gap-1">
            {action}
            <DisabledOptionHelp label={label} message={disabledMessage} />
          </div>
        );
      }

      return action;
    };

    return (
      <section
        className={`${className} flex w-full flex-col gap-2 rounded-lg border-0 bg-white p-4 shadow-[inset_0_0_0_1px_#e6e6e6,0_1px_2px_rgba(16,24,40,0.05)]`}
        data-testid="other-options"
      >
        <h3 className="m-0 font-inter text-[14px] font-semibold leading-5 text-neutral-800">
          Other Options
        </h3>

        <div className="grid w-full grid-cols-2 gap-2">
          {visibleFields.includes(FIELDS.LOCATION_TAG) && (
            <>
              {renderPlatformLabel("Location", FIELDS.LOCATION_TAG)}
              {isSummary ? (
                <p className="m-0 self-center font-inter text-xs font-medium leading-4 text-neutral-800">
                  {location?.name || "No location"}
                </p>
              ) : (
                renderAction({
                  label: `${location ? "Edit" : "Add"} location`,
                  onClick: dispatchLocationModal,
                  selectedLabel: location?.name,
                })
              )}
            </>
          )}

          {visibleFields.includes(FIELDS.COLLABORATOR_TAG) && (
            <>
              {renderPlatformLabel("Collaborators", FIELDS.COLLABORATOR_TAG)}
              {isSummary ? (
                <p className="m-0 self-center font-inter text-xs font-medium leading-4 text-neutral-800">
                  {!collaboratorIg || collaboratorIg.length === 0
                    ? "No collaborators"
                    : collaboratorIg.length === 1
                      ? "1 collaborator"
                      : `${collaboratorIg.length} collaborators`}
                </p>
              ) : (
                renderAction({
                  label: `${collaboratorIg?.length ? "Edit" : "Add"} collaborators`,
                  onClick: dispatchCollaboratorModal,
                  selectedLabel: collaboratorIg?.length
                    ? `${collaboratorIg.length} collaborator${
                        collaboratorIg.length === 1 ? "" : "s"
                      }`
                    : undefined,
                })
              )}
            </>
          )}

          {visibleFields.includes(FIELDS.USER_TAGS) && (
            <>
              {renderPlatformLabel("User tags", FIELDS.USER_TAGS)}
              {isSummary ? (
                <p className="m-0 self-center font-inter text-xs font-medium leading-4 text-neutral-800">
                  {hasUserTags
                    ? `${userTags.length} user tag${
                        userTags.length === 1 ? "" : "s"
                      }`
                    : "No user tags"}
                </p>
              ) : (
                renderAction({
                  label: `${hasUserTags ? "Edit" : "Add"} user tags`,
                  onClick: dispatchUserTagModal,
                  disabled: !userTagsCanBeChanged,
                  disabledMessage:
                    "User tags cannot be added if product tags are added. Remove product tags.",
                  selectedLabel: hasUserTags
                    ? `${userTags.length} user tag${
                        userTags.length === 1 ? "" : "s"
                      }`
                    : undefined,
                })
              )}
            </>
          )}

          {visibleFields.includes(FIELDS.PRODUCT_TAGS) && (
            <>
              {renderPlatformLabel("Product tags", FIELDS.PRODUCT_TAGS)}
              {isSummary ? (
                <p className="m-0 self-center font-inter text-xs font-medium leading-4 text-neutral-800">
                  {hasProductTags
                    ? `${productTags.length} product tag${
                        productTags.length === 1 ? "" : "s"
                      }`
                    : "No product tags"}
                </p>
              ) : (
                renderAction({
                  label: `${hasProductTags ? "Edit" : "Add"} product tags`,
                  onClick: dispatchProductTagModal,
                  disabled: !productTagsCanBeChanged,
                  disabledMessage:
                    "Product tags cannot be added if user tags are added. Remove user tags.",
                  selectedLabel: hasProductTags
                    ? `${productTags.length} product tag${
                        productTags.length === 1 ? "" : "s"
                      }`
                    : undefined,
                })
              )}
            </>
          )}

          {hasAlt && visibleFields.includes(FIELDS.ALT_TEXT) && (
            <>
              {renderPlatformLabel("Media alt text", FIELDS.ALT_TEXT)}
              {isSummary ? (
                <p
                  className="m-0 self-center font-inter text-xs font-medium leading-4 text-neutral-800"
                  data-testid="addEditAltTextLabel"
                >
                  {altText.length > 0
                    ? `${altText.length} alt text media`
                    : "No alt text"}
                </p>
              ) : (
                renderAction({
                  label: `${altTextCount > 0 ? "Edit" : "Add"} alt text`,
                  onClick: handleOpenAddAlt,
                  testId: "addEditAltTextButton",
                  selectedLabel:
                    altTextCount > 0
                      ? `${altTextCount} media with alt text`
                      : undefined,
                })
              )}
            </>
          )}
        </div>
        <ChunkLoadRecoveryModal open={requiresReload} onReload={reloadPage} />
      </section>
    );
  } else {
    return null;
  }
};

export default PostTags;
